// Sweet Match - funnel geometry, level generation, match/fail rules. No DOM.
//
// Everything here speaks in candy diameters (see physics.ts). The field is
// FIELD_W x FIELD_H units; index.ts picks the px-per-unit scale that fits the
// available play area.
//
// The match/fail rules follow in the next phase.

import {
  closestOnSegment,
  createWorld,
  makeBody,
  type Bounds,
  type Segment,
  type World,
} from "./physics";

/** Field width in candy diameters. Keep <= 7 or the tap targets get too small. */
export const FIELD_W = 6;
/** Field height. Aspect 6:11.2 fits a portrait play area without letterboxing. */
export const FIELD_H = 11.2;
/** Candy radius. Slightly under 0.5 so neighbours have a sliver of air. */
export const CANDY_R = 0.46;

/**
 * Neck width. 1.2 is not a taste call: two candies side by side need >= 2.0
 * units, so at 1.2 a pair can never enter the neck together - that is what
 * produces the single-file queue. Widening this breaks it; below ~1.05 a candy
 * jams against both walls at once.
 *
 * Note this only rules out jams *inside* the neck. Arching above it is handled
 * by the asymmetric funnel, see FUNNEL_ASYMMETRY.
 */
export const NECK_W = 1.2;

/**
 * Conservative lower bound on how many unmatched candies neck + pot hold.
 *
 * Historical note: under the original zone-match rule (two of a kind anywhere
 * in the queue pop), "types <= POT_CAPACITY" made overflow mathematically
 * impossible by pigeonhole. The touch-match rule (state.ts findMatch) killed
 * that guarantee on purpose: a stack like a,b,a,b,a holds five unmatched with
 * only two types, so *every* level can now be lost by stacking carelessly.
 * What POT_CAPACITY still does: gate the fail rule and the jam breaker (both
 * read "is the queue actually full?"), and anchor the difficulty curve's
 * comment below about when levels get mean.
 */
export const POT_CAPACITY = 5;

// Vertical stations, top to bottom.
const CHAMBER_L = 4.2; // left wall ends, left funnel edge starts
const CHAMBER_R = 4.9; // right wall ends - deliberately NOT the same, see below
const NECK_TOP = 7.0; // funnel edges meet the neck
const NECK_BOTTOM = 8.1; // neck ends, pot shoulders flare out
const POT_TOP = 8.8; // shoulders end, pot walls start
/**
 * Pot width 1.5: like the neck, this is not a taste call. Two candies side by
 * side need 2*2r + 2r-to-wall = 1.84 units, so at 1.5 the whole column from
 * neck entrance to pot floor is a single-file stack. That is what makes the
 * touch-match rule (see state.ts findMatch) a readable game: a new arrival can
 * only ever touch the top of the stack, so "will this pop?" is answerable at a
 * glance. The old 3.0-wide pot let candies spread into a heap where almost
 * everything touched, which made order nearly irrelevant.
 */
const POT_X0 = 2.25;
const POT_X1 = 3.75;

const NECK_X0 = (FIELD_W - NECK_W) / 2;
const NECK_X1 = (FIELD_W + NECK_W) / 2;

/**
 * The two funnel edges have different slopes on purpose, and this is load
 * bearing - not decoration.
 *
 * Two candies touching each other and each touching one funnel edge form an
 * arch. Vertical balance on one candy needs `N·cos θ = mg`, which leaves a
 * horizontal `mg·tan θ` to be carried by the other candy. Both sides must push
 * equally hard, so a static arch exists exactly when `tan θ_left = tan θ_right`
 * - at *any* steepness, which is why making the funnel steeper does not help.
 * Our solver has no static friction (the tangential damping is velocity based,
 * so it produces zero force at standstill), so unequal slopes leave the arch
 * with no equilibrium at all: it always collapses.
 *
 * Left edge ≈ 49°, right edge ≈ 41° from horizontal. Validator check 5 fails if
 * these are ever made equal again.
 */
export const FUNNEL_ASYMMETRY = CHAMBER_R - CHAMBER_L;

function seg(ax: number, ay: number, bx: number, by: number): Segment {
  return { ax, ay, bx, by };
}

/**
 * The static playfield: chamber walls, 45° funnel edges, neck, pot shoulders,
 * pot walls, floor. Deterministic - same list every call.
 */
export function funnelSegments(): Segment[] {
  return [
    // Upper chamber (where the untapped candies float).
    seg(0, 0, 0, CHAMBER_L),
    seg(FIELD_W, 0, FIELD_W, CHAMBER_R),
    // Funnel edges - steep enough to slide, unequal so arches cannot hold.
    seg(0, CHAMBER_L, NECK_X0, NECK_TOP),
    seg(FIELD_W, CHAMBER_R, NECK_X1, NECK_TOP),
    // Neck: the single-file queue.
    seg(NECK_X0, NECK_TOP, NECK_X0, NECK_BOTTOM),
    seg(NECK_X1, NECK_TOP, NECK_X1, NECK_BOTTOM),
    // Pot shoulders.
    seg(NECK_X0, NECK_BOTTOM, POT_X0, POT_TOP),
    seg(NECK_X1, NECK_BOTTOM, POT_X1, POT_TOP),
    // Pot walls + floor.
    seg(POT_X0, POT_TOP, POT_X0, FIELD_H),
    seg(POT_X1, POT_TOP, POT_X1, FIELD_H),
    seg(POT_X0, FIELD_H, POT_X1, FIELD_H),
  ];
}

export function worldBounds(): Bounds {
  return { x0: 0, y0: 0, x1: FIELD_W, y1: FIELD_H };
}

/**
 * The match zone: everything from the neck entrance down. A pair only pops
 * when it *touches* inside this column (state.ts findMatch); the zone keeps
 * mid-air contacts up in the funnel cone from counting, so a match always
 * reads as "they met in the pot".
 */
export function potZone(): Bounds {
  return { x0: POT_X0, y0: NECK_TOP, x1: POT_X1, y1: FIELD_H };
}

/**
 * Fail line. A *dropped* candy that comes to rest with its center above this is
 * backing up into the funnel: the queue is full. Untapped candies float above
 * this line by definition and must never be counted.
 */
export function limitLineY(): number {
  return NECK_TOP;
}

/** Where generated candies may be placed (chamber + the wide part of the cone). */
export function spawnBand(): { top: number; bottom: number } {
  return { top: 0.5, bottom: NECK_TOP - 1.2 };
}

/** Horizontal span of the funnel at a given height, for spawn placement. */
export function funnelSpanAt(y: number): { x0: number; x1: number } {
  const tl = Math.min(1, Math.max(0, (y - CHAMBER_L) / (NECK_TOP - CHAMBER_L)));
  const tr = Math.min(1, Math.max(0, (y - CHAMBER_R) / (NECK_TOP - CHAMBER_R)));
  return { x0: NECK_X0 * tl, x1: FIELD_W - (FIELD_W - NECK_X1) * tr };
}

export function inZone(x: number, y: number, z: Bounds): boolean {
  return x >= z.x0 && x <= z.x1 && y >= z.y0 && y <= z.y1;
}

/** An empty world with the funnel in place. */
export function createFunnelWorld(): World {
  return createWorld(funnelSegments(), worldBounds());
}

/** Add a candy-sized body. `dynamic` false = floating, waiting for a tap. */
export function addCandyBody(world: World, id: number, x: number, y: number, dynamic = false) {
  const b = makeBody(id, x, y, CANDY_R, dynamic);
  world.bodies.push(b);
  return b;
}

// ---- Jam breaker ------------------------------------------------------------

/** How long a candy must stand still above the fail line to count as jammed. */
const JAM_SETTLED_MS = 400;
/**
 * Sideways velocity handed to a jammed candy, in units per step. 0.015 was not
 * enough: because the direction flips every tick the candy just oscillates in
 * place. 0.04 clears every measured pile (16/20/24) - see validator check 13.
 */
const JAM_NUDGE = 0.04;

/**
 * Break a granular bridge above the neck.
 *
 * The asymmetric funnel (see FUNNEL_ASYMMETRY) kills a two-candy arch, but a
 * *loaded* pile is a different problem: with 20+ candies pressing together the
 * contact network has enough force paths to find an equilibrium anyway, and the
 * pile bridges the neck entrance permanently. Measured threshold: piles up to 16
 * always drain, 20 leaves 11 stuck, 24 leaves 18.
 *
 * Real play never reaches that - the player taps one candy at a time, and the
 * fail rule fires long before a pile that size can build up (validator check 13
 * plays whole levels one tap at a time and drains them completely). This exists
 * so the recoverable case is airtight: if a match frees space, the stack *must*
 * sink, otherwise the LoseTimer would be a death sentence instead of a warning.
 *
 * Gated on the pot having room, which is what keeps it honest: a backup above a
 * *full* pot is the real fail condition and must not be nudged away. Directions
 * alternate per candy and per tick, so the nudge shears the pile apart instead
 * of sliding it down as a block, and stays deterministic.
 *
 * Returns how many candies were nudged (0 in the overwhelming majority of frames).
 */
export function breakJams(world: World, tick: number): number {
  const zone = potZone();
  let inPot = 0;
  for (const b of world.bodies) {
    if (b.dynamic && inZone(b.x, b.y, zone)) inPot++;
  }
  if (inPot >= POT_CAPACITY) return 0; // legitimate backup - leave it to the fail rule

  const limit = limitLineY();
  let nudged = 0;
  for (const b of world.bodies) {
    if (!b.dynamic || b.y >= limit || b.settledMs < JAM_SETTLED_MS) continue;
    const dir = ((b.id + tick) & 1) === 0 ? 1 : -1;
    b.px -= dir * JAM_NUDGE; // velocity is x - px, so this pushes along `dir`
    // Deliberately NOT resetting settledMs here: the nudge makes the body move,
    // so the solver clears it on the next step anyway. Clearing it by hand would
    // let the jam breaker erase the very signal the fail rule reads.
    nudged++;
  }
  return nudged;
}

// ---- Deterministic RNG (mulberry32, same scheme as the other games) ---------

export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Own seed stream (blockjam/bubblepop use 7000/9000). */
function levelSeed(n: number): number {
  return (11000 + n * 2654435761) >>> 0;
}

// ---- Level generation -------------------------------------------------------

/**
 * Hard ceiling on candies. The spawn area fits ~30 slots, so 24 leaves the blob
 * growth room to pick an interesting shape instead of being forced to take
 * every last slot. Past level ~30 the difficulty comes from types and specials,
 * not from more candies.
 */
export const MAX_PAIRS = 12;
/** Distinct candy types the art layer must be able to draw. */
export const MAX_TYPES = 8;

export interface SmSpawn {
  x: number;
  y: number;
  type: number;
  /** Identity hidden behind a "?" until tapped. Cosmetic: `type` is already fixed. */
  mystery: boolean;
  /** Encased in ice; must be hit before it can fall. */
  frozen: boolean;
}

export interface SmLevel {
  n: number;
  pairs: number;
  types: number;
  spawn: SmSpawn[];
  /** Stack-pressure target for 3 stars, see the stars rule. */
  peakPar: number;
}

export function shapeForSmLevel(n: number): {
  pairs: number;
  types: number;
  mystery: number;
  frozen: number;
} {
  const pairs = Math.min(MAX_PAIRS, 3 + Math.floor(n / 3));
  // Types is the real difficulty knob: more types = smaller chance the top of
  // the stack is your partner. Under touch-matching every level is losable,
  // but few types keep the early levels forgiving; stays <= POT_CAPACITY
  // through level 35 so the curve ramps instead of jumping.
  const types = Math.min(MAX_TYPES, 3 + Math.floor(n / 12));
  const mystery = n < 5 ? 0 : Math.min(3, 1 + Math.floor(n / 20));
  const frozen = n < 30 ? 0 : Math.min(4, 1 + Math.floor((n - 30) / 15));
  return { pairs, types, mystery, frozen };
}

/**
 * How deep the *designed* solution nests, by level. Under touch-matching the
 * queue is bracket matching: an unmatched drop opens a bracket, its partner
 * landing right on top closes it. Depth 1 means the designed line is "drop
 * pairs back to back" (tutorial); deeper caps force the player to hold several
 * open brackets before the first close. One below POT_CAPACITY so the designed
 * line itself never brushes overflow.
 */
export function depthCapFor(n: number): number {
  return Math.min(POT_CAPACITY - 1, 1 + Math.floor(n / 8));
}

/**
 * Assign types along a constructed winning line, or null on a (rare) draw
 * conflict - the caller retries.
 *
 * Under touch-matching, randomly-typed boards are NOT reliably winnable: which
 * bracket may open first depends on the unblocking order of the pile, and a
 * wrong structure forces crossing brackets, which are permanently dead. So the
 * generator works backwards: draw a random *playable tap order* (a topological
 * order of the who-blocks-whom DAG), walk it as a balanced-bracket sequence
 * within the depth cap, and let that walk decide the types. The walk IS a
 * winning line, so every level ships provably winnable - no solver at runtime,
 * still a pure function of the level number.
 */
function buildWinnableTypes(
  blockedBy: number[][],
  pairs: number,
  types: number,
  depthCap: number,
  rng: () => number,
): { typeAt: number[]; maxDepth: number } | null {
  const count = blockedBy.length;
  // Pool of pair types: every type at least one pair, remainder random (same
  // distribution the old multiset had), shuffled so draws are uniform.
  const perType = new Array<number>(types).fill(1);
  for (let i = types; i < pairs; i++) perType[Math.floor(rng() * types)]++;
  const pool = shuffle(
    perType.flatMap((k, t) => new Array<number>(k).fill(t)),
    rng,
  );

  // Random topological order over the blocker DAG = a physically playable tap
  // order (a candy may go once everything below it in its column is gone).
  const blocks: number[][] = blockedBy.map(() => []);
  const pending = blockedBy.map((b) => b.length);
  blockedBy.forEach((bs, i) => {
    for (const j of bs) blocks[j].push(i);
  });
  const ready: number[] = [];
  for (let i = 0; i < count; i++) if (pending[i] === 0) ready.push(i);
  const ordered: number[] = [];
  while (ready.length > 0) {
    const i = ready.splice(Math.floor(rng() * ready.length), 1)[0];
    ordered.push(i);
    for (const j of blocks[i]) if (--pending[j] === 0) ready.push(j);
  }
  if (ordered.length < count) return null; // cycle - impossible with y-ordering, but never trust it silently

  // Bracket walk: open = start a new pair (consumes a pool entry), close =
  // finish the top pair. An open must differ from the current top, otherwise
  // the arrival would touch-match instead of landing (adjacent equals cannot
  // exist in a real stack).
  const typeAt = new Array<number>(count).fill(-1);
  const stack: number[] = [];
  let maxDepth = 0;
  for (let k = 0; k < count; k++) {
    const left = count - k; // still to place, including this one
    const canClose = stack.length > 0;
    // After an open there must be room for its close: opens <= slots left.
    const canOpen = pool.length > 0 && stack.length < depthCap && stack.length + 1 <= left - 1;
    if (!canOpen && !canClose) return null;
    const close = !canOpen || (canClose && rng() < 0.5);
    if (close) {
      typeAt[ordered[k]] = stack.pop()!;
    } else {
      const top = stack.length > 0 ? stack[stack.length - 1] : -1;
      const pi = pool.findIndex((t) => t !== top);
      if (pi < 0) return null; // only the top's own type left in the pool - retry
      const t = pool.splice(pi, 1)[0];
      typeAt[ordered[k]] = t;
      stack.push(t);
      if (stack.length > maxDepth) maxDepth = stack.length;
    }
  }
  if (stack.length > 0 || pool.length > 0) return null; // bookkeeping guard
  return { typeAt, maxDepth };
}

function shuffle<T>(items: T[], rng: () => number): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const SLOT_PITCH = 2 * CANDY_R + 0.08; // 0.99: centers this far apart never overlap
const SLOT_JITTER = 0.03; // keeps 0.99 - 2*0.03 = 0.93 > 2r, so still no overlap
const WALL_MARGIN = 0.04; // clearance to the funnel on top of the radius

/** Is this spot far enough from every wall to spawn a candy? */
export function clearOfWalls(x: number, y: number, segments: Segment[]): boolean {
  const need = CANDY_R + WALL_MARGIN;
  return segments.every((s) => closestOnSegment(x, y, s).dist >= need);
}

/**
 * Candidate positions: a staggered grid over the spawn band, jittered, with
 * anything too close to a wall dropped. Perpendicular distance to the slanted
 * funnel edges is what matters here - a horizontal margin would let candies
 * clip into the cone.
 */
function buildSlots(rng: () => number): Array<{ x: number; y: number }> {
  const segments = funnelSegments();
  const band = spawnBand();
  const slots: Array<{ x: number; y: number }> = [];
  let row = 0;
  for (let y = band.top; y <= band.bottom; y += SLOT_PITCH * 0.96, row++) {
    const span = funnelSpanAt(y);
    const stagger = (row % 2) * SLOT_PITCH * 0.5;
    const from = span.x0 + CANDY_R + WALL_MARGIN + stagger;
    const to = span.x1 - CANDY_R - WALL_MARGIN;
    for (let x = from; x <= to; x += SLOT_PITCH) {
      const jx = x + (rng() * 2 - 1) * SLOT_JITTER;
      const jy = y + (rng() * 2 - 1) * SLOT_JITTER;
      if (clearOfWalls(jx, jy, segments)) slots.push({ x: jx, y: jy });
    }
  }
  return slots;
}

/**
 * Grow a connected blob of `count` slots. Connected on purpose: a scattered
 * arrangement gives every candy a free fall path, which makes the level
 * trivial. The puzzle only exists when candies sit above each other and block
 * one another, so the cluster has to be compact.
 */
function growCluster(
  slots: Array<{ x: number; y: number }>,
  count: number,
  rng: () => number,
): number[] {
  const neighbours: number[][] = slots.map(() => []);
  const reach = SLOT_PITCH * 1.45;
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      if (Math.hypot(slots[i].x - slots[j].x, slots[i].y - slots[j].y) <= reach) {
        neighbours[i].push(j);
        neighbours[j].push(i);
      }
    }
  }

  const taken = new Set<number>();
  // Start low in the band, so the cluster hangs over the funnel mouth rather
  // than in a corner of the ceiling.
  const lowest = slots
    .map((s, i) => ({ i, y: s.y }))
    .sort((a, b) => b.y - a.y)
    .slice(0, Math.max(1, Math.floor(slots.length / 3)));
  let frontier = [lowest[Math.floor(rng() * lowest.length)].i];

  while (taken.size < count && frontier.length > 0) {
    const pick = Math.floor(rng() * frontier.length);
    const idx = frontier[pick];
    frontier.splice(pick, 1);
    if (taken.has(idx)) continue;
    taken.add(idx);
    for (const nb of neighbours[idx]) if (!taken.has(nb)) frontier.push(nb);
  }
  // Blob ran dry before reaching count (shouldn't happen with the current
  // geometry, but never silently ship a level with missing candies).
  for (let i = 0; i < slots.length && taken.size < count; i++) taken.add(i);
  return [...taken].slice(0, count);
}

/** Generate one level deterministically from its number. */
export function generateSmLevel(n: number): SmLevel {
  const { pairs, types, mystery, frozen } = shapeForSmLevel(n);
  const rng = makeRng(levelSeed(n));

  const count = pairs * 2;
  const slots = buildSlots(rng);
  const chosen = growCluster(slots, count, rng);
  const pos = chosen.map((idx) => slots[idx]);

  // Who blocks whom: the same column test the winnability planner and a
  // squinting player use - a candy is tappable once nothing sits below it.
  const col = 2 * CANDY_R * 0.9;
  const blockedBy: number[][] = pos.map((p, i) =>
    pos.map((_, j) => j).filter((j) => j !== i && pos[j].y > p.y && Math.abs(pos[j].x - p.x) < col),
  );

  // Types come from a constructed winning line (see buildWinnableTypes). The
  // walk can hit a rare draw conflict; retrying advances the rng, and depth 1
  // (strict open-close alternation) cannot conflict, so the fallback always
  // lands. The throw only guards that invariant.
  let built: { typeAt: number[]; maxDepth: number } | null = null;
  for (let attempt = 0; attempt < 40 && !built; attempt++) {
    built = buildWinnableTypes(blockedBy, pairs, types, depthCapFor(n), rng);
  }
  for (let attempt = 0; attempt < 40 && !built; attempt++) {
    built = buildWinnableTypes(blockedBy, pairs, types, 1, rng);
  }
  if (!built) throw new Error("generateSmLevel: could not construct a winnable level");

  const spawn: SmSpawn[] = pos.map((p, i) => ({
    x: p.x,
    y: p.y,
    type: built.typeAt[i],
    mystery: false,
    frozen: false,
  }));

  // Specials land on disjoint candies.
  const order = shuffle(
    spawn.map((_, i) => i),
    rng,
  );
  let cursor = 0;
  for (let k = 0; k < mystery && cursor < order.length; k++) spawn[order[cursor++]].mystery = true;
  for (let k = 0; k < frozen && cursor < order.length; k++) spawn[order[cursor++]].frozen = true;

  // Stars measure peak stack pressure. The designed line reaches maxDepth on
  // the stack, and a closer in transit through the zone counts as +1 pressure
  // for a moment, so par = maxDepth + 1 is "played as well as the designer".
  // Floor of 2 because that transit +1 exists even on depth-1 tutorials.
  return { n, pairs, types, spawn, peakPar: Math.max(2, built.maxDepth + 1) };
}

/** Build a world with the level's candies floating in place, waiting for taps. */
export function buildLevelWorld(level: SmLevel): World {
  const world = createFunnelWorld();
  level.spawn.forEach((s, i) => addCandyBody(world, i + 1, s.x, s.y, false));
  return world;
}
